import { LegendList, type LegendListRef } from "@legendapp/list/react";
import type { WorkLogEntry } from "../../session-logic";
import { useLayoutEffect, useRef, useState } from "react";
import type { WorkGroupViewState } from "./TimelineRowContext";
import {
  resolveWorkGroupScrollIndex,
  shouldFollowWorkGroupAppend,
} from "./MessagesTimeline.labels";
import { resolveWorkGroupScrollAnchor } from "./timelineScrollAnchoring";
import { SimpleWorkEntryRow } from "./TimelineWorkEntryRows";

/**
 * One bounded, virtualized expanded tool group (ticket 3525, upstream
 * `ExpandedWorkGroupEntries`): scrolls to the saved entry, follows appended
 * calls only at the hard end, and fades at the scroll edges.
 */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.tsx at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function ExpandedWorkGroupEntries({
  anchorKey,
  entries,
  workspaceRoot,
  viewState,
}: {
  anchorKey: string;
  entries: WorkLogEntry[];
  workspaceRoot: string | undefined;
  viewState: WorkGroupViewState;
}) {
  const [initialScrollIndex] = useState(() =>
    resolveWorkGroupScrollIndex(entries, viewState.scrollPositions.get(anchorKey)),
  );
  const [restoringPosition, setRestoringPosition] = useState(initialScrollIndex !== undefined);
  const listRef = useRef<LegendListRef>(null);
  const [fades, setFades] = useState({ top: false, bottom: false, viewportHeight: 0 });
  const [appendState, setAppendState] = useState({ entries, follow: false });
  // Capture the pre-change edge once per incoming array, before new layout
  // metrics arrive. Edge/viewport changes never turn a status update into a follow.
  if (appendState.entries !== entries) {
    setAppendState({
      entries,
      follow:
        fades.viewportHeight > 0 &&
        shouldFollowWorkGroupAppend(appendState.entries, entries, fades.bottom ? Infinity : 0),
    });
  }

  const updateScrollFades = () => {
    const element = listRef.current?.getScrollableNode();
    if (!element) return;
    const distanceFromEnd = element.scrollHeight - element.clientHeight - element.scrollTop;
    const viewportHeight = element.clientHeight;
    const top = element.scrollTop > 1;
    const bottom = distanceFromEnd > 1;
    setFades((previous) =>
      previous.top === top &&
      previous.bottom === bottom &&
      previous.viewportHeight === viewportHeight
        ? previous
        : { top, bottom, viewportHeight },
    );
  };

  const handleScroll = () => {
    const state = listRef.current?.getState();
    const position = state && resolveWorkGroupScrollAnchor(state);
    if (position) {
      viewState.scrollPositions.set(anchorKey, {
        entryId: position.rowId,
        offset: position.offsetWithinRow,
      });
    }
    updateScrollFades();
  };

  const handleLoad = () => {
    setRestoringPosition(false);
  };

  useLayoutEffect(() => {
    const element = listRef.current?.getScrollableNode();
    if (!element) return;
    updateScrollFades();
    const observer = new ResizeObserver(updateScrollFades);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => observer.disconnect();
  });

  const renderEntry = ({ item }: { item: WorkLogEntry }) => (
    <SimpleWorkEntryRow
      key={item.id}
      workEntry={item}
      workspaceRoot={workspaceRoot}
      isExpandedToolGroupEntry
    />
  );

  return (
    <div className="relative">
      {fades.top ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-background to-transparent"
        />
      ) : null}
      {fades.bottom ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 bg-gradient-to-t from-background to-transparent"
        />
      ) : null}
      <LegendList<WorkLogEntry>
        ref={listRef}
        data={entries}
        keyExtractor={(entry) => entry.id}
        renderItem={renderEntry}
        estimatedItemSize={24}
        drawDistance={240}
        recycleItems
        {...(initialScrollIndex ? { initialScrollIndex } : {})}
        maintainScrollAtEnd={
          appendState.follow ? { animated: false, on: { dataChange: true } } : false
        }
        maintainScrollAtEndThreshold={1 / Math.max(1, fades.viewportHeight)}
        {...(restoringPosition && initialScrollIndex
          ? { alwaysRender: { indices: [initialScrollIndex.index] } }
          : {})}
        maintainVisibleContentPosition
        onLoad={handleLoad}
        onScroll={handleScroll}
        onLayout={updateScrollFades}
        tabIndex={0}
        role="region"
        aria-label="Tool calls"
        data-tool-group-scroll
        className="max-h-[min(18rem,50dvh)] scroll-py-6 overflow-x-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70"
      />
    </div>
  );
}
